import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Chip,
  CircularProgress,
  IconButton,
  Paper,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import TodayIcon from '@mui/icons-material/Today';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import {
  TimelineCalendar,
  TimelineItem,
  TimelineRow,
  TimelineRowGroup,
} from 'mq-timeline-calendar/react';
import {
  addDays,
  addMonths,
  addWeeks,
  endOfDay,
  endOfMonth,
  endOfWeek,
  format,
  startOfDay,
  startOfMonth,
  startOfWeek,
  subMonths,
  subWeeks,
} from 'date-fns';
import { EmptyState } from '@/components/common/EmptyState';
import {
  CourtesyLoanDialog,
  COURTESY_LOAN_BAR_COLOR,
} from '@/components/workorders/courtesy';
import type { CourtesyLoanDialogMode } from '@/components/workorders/courtesy';
import {
  courtesyLoanBarRange,
  courtesyLoanFromConflict,
  useCourtesyAvailability,
  type CourtesyLoan,
} from '@/hooks/useCourtesyVehicles';
import { useAuthStore } from '@/stores/authStore';
import { useIsMobile } from '@/hooks/useIsMobile';
import { useUIStore } from '@/stores/uiStore';
import { useSwipe } from '@/hooks/useSwipe';
import { calendarLocaleFor } from './supervisorTaskCalendar';
import { currentLanguage, dateFnsLocale } from '@/utils/locale';

export type CourtesyCalendarView = 'day' | 'week' | 'month';

export function courtesyCalendarRange(
  currentDate: Date,
  view: CourtesyCalendarView,
): { startDate: Date; endDate: Date } {
  switch (view) {
    case 'day':
      return { startDate: startOfDay(currentDate), endDate: endOfDay(currentDate) };
    case 'week': {
      const weekStart = startOfWeek(currentDate, { weekStartsOn: 1 });
      return {
        startDate: weekStart,
        endDate: endOfWeek(currentDate, { weekStartsOn: 1 }),
      };
    }
    case 'month':
      return { startDate: startOfMonth(currentDate), endDate: endOfMonth(currentDate) };
  }
}

interface CourtesyCalendarTabProps {
  fillHeight?: boolean;
}

export function CourtesyCalendarTab({ fillHeight = false }: CourtesyCalendarTabProps) {
  const { t } = useTranslation('calendar');
  const { t: tw } = useTranslation('workorders');
  const { t: tc } = useTranslation('common');
  const navigate = useNavigate();
  const themeMode = useUIStore((s) => s.themeMode);
  const isMobile = useIsMobile();
  const hasPermission = useAuthStore((s) => s.hasPermission);
  const canUpdate = hasPermission('work_orders.update');
  const fill = fillHeight && !isMobile;
  const calendarHeight = fill
    ? '100%'
    : isMobile
      ? 'calc(100vh - 290px)'
      : 'calc(100vh - 370px)';

  const [currentDate, setCurrentDate] = useState(new Date());
  const [view, setView] = useState<CourtesyCalendarView>(() => (isMobile ? 'day' : 'week'));
  const [dialogLoan, setDialogLoan] = useState<CourtesyLoan | null>(null);
  const [dialogMode, setDialogMode] = useState<CourtesyLoanDialogMode>('view');

  const { startDate, endDate } = useMemo(
    () => courtesyCalendarRange(currentDate, view),
    [currentDate, view],
  );

  const { data: cars, isLoading } = useCourtesyAvailability(
    startDate.toISOString(),
    endDate.toISOString(),
  );

  const handlePrevious = useCallback(() => {
    switch (view) {
      case 'day':
        setCurrentDate((d) => addDays(d, -1));
        break;
      case 'week':
        setCurrentDate((d) => subWeeks(d, 1));
        break;
      case 'month':
        setCurrentDate((d) => subMonths(d, 1));
        break;
    }
  }, [view]);

  const handleNext = useCallback(() => {
    switch (view) {
      case 'day':
        setCurrentDate((d) => addDays(d, 1));
        break;
      case 'week':
        setCurrentDate((d) => addWeeks(d, 1));
        break;
      case 'month':
        setCurrentDate((d) => addMonths(d, 1));
        break;
    }
  }, [view]);

  const handleToday = useCallback(() => {
    setCurrentDate(new Date());
  }, []);

  const handleSwipe = useCallback(
    (deltaX: number) => {
      if (deltaX < 0) handleNext();
      else handlePrevious();
    },
    [handleNext, handlePrevious],
  );
  const swipeHandlers = useSwipe({ onSwipe: handleSwipe, threshold: 50 });

  const loanCount = (cars ?? []).filter((car) => car.conflict).length;

  const dateLabel = (() => {
    switch (view) {
      case 'day':
        return format(currentDate, 'EEEE d.M.yyyy', { locale: dateFnsLocale() });
      case 'week': {
        const ws = startOfWeek(currentDate, { weekStartsOn: 1 });
        const we = endOfWeek(currentDate, { weekStartsOn: 1 });
        return `${format(ws, 'd.M.')} – ${format(we, 'd.M.yyyy')}`;
      }
      case 'month':
        return format(currentDate, 'LLLL yyyy', { locale: dateFnsLocale() });
    }
  })();

  if (isLoading && !cars) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }} role="status" aria-label={tc('loading')}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box
      className="os-timeline-root"
      sx={
        fill
          ? { flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }
          : undefined
      }
    >
      <Stack direction="row" spacing={2} sx={{ mb: 2, flexShrink: 0 }} alignItems="center" flexWrap="wrap">
        <Stack direction="row" spacing={0.5} alignItems="center">
          <Tooltip title={tc('previous')}>
            <IconButton onClick={handlePrevious} size="small" aria-label={tc('previous')}>
              <ChevronLeftIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title={tc('today')}>
            <IconButton onClick={handleToday} size="small" aria-label={tc('today')}>
              <TodayIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title={tc('next')}>
            <IconButton onClick={handleNext} size="small" aria-label={tc('next')}>
              <ChevronRightIcon />
            </IconButton>
          </Tooltip>
        </Stack>

        <Typography variant="h6" sx={{ minWidth: 200, textTransform: 'capitalize' }}>
          {dateLabel}
        </Typography>

        <Stack direction="row" spacing={1} sx={{ ml: 'auto !important' }}>
          {(['day', 'week', 'month'] as CourtesyCalendarView[]).map((item) => (
            <Chip
              key={item}
              label={tc(item)}
              onClick={() => { setView(item); }}
              color={view === item ? 'primary' : 'default'}
              variant={view === item ? 'filled' : 'outlined'}
              size="small"
            />
          ))}
        </Stack>
      </Stack>

      {(cars ?? []).length === 0 ? (
        <EmptyState
          icon={<DirectionsCarIcon />}
          title={t('courtesy.noCars')}
          description={t('courtesy.noCarsDescription')}
          action={{
            label: t('courtesy.addCar'),
            onClick: () => { void navigate('/settings?tab=courtesy'); },
          }}
          minHeight={200}
        />
      ) : (
        <Paper
          elevation={0}
          sx={{
            border: 1,
            borderColor: 'divider',
            borderRadius: 2,
            overflow: 'hidden',
            touchAction: isMobile ? 'pan-y' : 'auto',
            ...(fill && { flexGrow: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }),
          }}
          {...(isMobile ? swipeHandlers : {})}
        >
          <TimelineCalendar
            startDate={startDate}
            endDate={endDate}
            height={calendarHeight}
            showCurrentTime
            locale={calendarLocaleFor(currentLanguage())}
            theme={themeMode}
            compressClosedHours={false}
            sidebar={
              !isMobile && (cars ?? []).length > 1
                ? {
                    width: 160,
                    content: (
                      <>
                        {(cars ?? []).map((car) => (
                          <Box
                            key={car.id}
                            sx={{
                              height: 'var(--timeline-row-height, 60px)',
                              display: 'flex',
                              alignItems: 'center',
                              px: 1.5,
                              borderBottom: 1,
                              borderColor: 'divider',
                              boxSizing: 'border-box',
                            }}
                          >
                            <Box sx={{ minWidth: 0 }}>
                              <Typography variant="caption" fontWeight={600} noWrap lineHeight={1.2}>
                                {car.registrationNumber}
                              </Typography>
                              {car.displayName && (
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                  noWrap
                                  lineHeight={1.2}
                                  display="block"
                                >
                                  {car.displayName}
                                </Typography>
                              )}
                            </Box>
                          </Box>
                        ))}
                      </>
                    ),
                  }
                : undefined
            }
          >
            <TimelineRowGroup>
              {(cars ?? []).map((car) => {
                const loan = courtesyLoanFromConflict(car);
                const bar = loan
                  ? courtesyLoanBarRange({
                      plannedStart: loan.plannedStart,
                      plannedEnd: loan.plannedEnd,
                      status: loan.status,
                      outAt: loan.outAt,
                      returnedAt: loan.returnedAt,
                    })
                  : null;
                return (
                  <TimelineRow
                    key={car.id}
                    id={car.id}
                    label={
                      isMobile
                        ? car.registrationNumber
                        : car.displayName
                          ? `${car.registrationNumber} – ${car.displayName}`
                          : car.registrationNumber
                    }
                    rowCount={1}
                    height={isMobile ? 60 : undefined}
                    collapsible={false}
                    style={{
                      borderBottom: `1px solid ${themeMode === 'dark' ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.12)'}`,
                    }}
                  >
                    {loan && bar && (
                      <TimelineItem startTime={bar.start} endTime={bar.end} row={0}>
                        <Box
                          onClick={() => {
                            setDialogLoan(loan);
                            setDialogMode('view');
                          }}
                          sx={{
                            height: '100%',
                            px: 1,
                            display: 'flex',
                            alignItems: 'center',
                            bgcolor: COURTESY_LOAN_BAR_COLOR[loan.status],
                            color: 'primary.contrastText',
                            cursor: 'pointer',
                            borderRadius: 0.5,
                            overflow: 'hidden',
                          }}
                        >
                          <Typography variant="caption" noWrap>
                            {t('courtesy.loanBar', {
                              plate: car.registrationNumber,
                              status: tw(`courtesy.status.${loan.status}`),
                            })}
                          </Typography>
                        </Box>
                      </TimelineItem>
                    )}
                  </TimelineRow>
                );
              })}
            </TimelineRowGroup>
          </TimelineCalendar>
        </Paper>
      )}

      {(cars ?? []).length > 0 && loanCount === 0 && (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Typography color="text.secondary">{t('courtesy.noLoans')}</Typography>
        </Box>
      )}

      <CourtesyLoanDialog
        open={dialogLoan != null}
        loan={dialogLoan}
        mode={dialogMode}
        canUpdate={canUpdate}
        onClose={() => { setDialogLoan(null); }}
        onRequestMode={(next) => { setDialogMode(next); }}
      />
    </Box>
  );
}
